// Разбор и подготовка пользовательского GeoJSON-файла, перетащенного на карту.
//
// Поддерживается только WGS84 (EPSG:4326) — как того требует спецификация GeoJSON.
// Файлы в проекции метров (частая выгрузка из QGIS в EPSG:3857) отсекаются проверкой
// диапазона координат: молча отрисовать их нельзя, они улетят за пределы мира.
import { t } from '@lingui/macro';
import {
    USER_GEOJSON_MAX_BYTES,
    USER_GEOJSON_EXTENSIONS,
    USER_LAYER_COLOR,
    USER_LAYER_FILL_OPACITY,
    USER_LAYER_PREFIX,
} from '../constants/userLayers';

// Имя слоя = имя файла без расширения: features.geojson → features
export const layerNameFromFile = fileName => fileName.replace(/\.[^.]+$/, '');

const hasAllowedExtension = fileName => USER_GEOJSON_EXTENSIONS
    .some(ext => fileName.toLowerCase().endsWith(ext));
// Обход координат любой вложенности (Point → MultiPolygon) с ранним выходом.
const forEachPosition = (coords, fn) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === 'number') {
        fn(coords);
        return;
    }
    for (const item of coords) forEachPosition(item, fn);
};
const forEachGeometry = (geometry, fn) => {
    if (!geometry) return;
    if (geometry.type === 'GeometryCollection') {
        (geometry.geometries || []).forEach(g => forEachGeometry(g, fn));
        return;
    }
    fn(geometry);
};
const featuresOf = data => {
    if (!data || typeof data !== 'object') return [];
    if (data.type === 'FeatureCollection') return Array.isArray(data.features) ? data.features : [];
    if (data.type === 'Feature') return [ data ];
    // Голая геометрия — заворачиваем в фичу, чтобы дальше работать единообразно
    if (typeof data.type === 'string') return [{ type: 'Feature', geometry: data, properties: {} }];
    return [];
};

// Приводим что угодно валидное (FeatureCollection / Feature / геометрия) к FeatureCollection.
export const toFeatureCollection = data => ({
    type: 'FeatureCollection',
    features: featuresOf(data),
});

const GEOMETRY_KIND = {
    Point: 'point',
    MultiPoint: 'point',
    LineString: 'line',
    MultiLineString: 'line',
    Polygon: 'polygon',
    MultiPolygon: 'polygon',
};

// Какие геометрии реально присутствуют в файле — по ним решаем, сколько mapbox-слоёв создавать.
export const geometryKinds = featureCollection => {
    const kinds = new Set();
    featureCollection.features.forEach(({ geometry }) => {
        forEachGeometry(geometry, g => {
            const kind = GEOMETRY_KIND[g.type];
            if (kind) kinds.add(kind);
        });
    });
    return kinds;
};

// Bbox в формате mapbox fitBounds: [[west, south], [east, north]]; null — если координат нет.
export const featureCollectionBounds = featureCollection => {
    let west = Infinity; let south = Infinity; let east = -Infinity; let north = -Infinity;
    featureCollection.features.forEach(({ geometry }) => {
        forEachGeometry(geometry, g => forEachPosition(g.coordinates, ([ lng, lat ]) => {
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
            if (lng < west) west = lng;
            if (lng > east) east = lng;
            if (lat < south) south = lat;
            if (lat > north) north = lat;
        }));
    });
    if (!Number.isFinite(west) || !Number.isFinite(south)) return null;
    return [ [ west, south ], [ east, north ] ];
};

// Грубая проверка на WGS84: все координаты обязаны укладываться в ±180 / ±90.
const isWgs84 = featureCollection => {
    const bounds = featureCollectionBounds(featureCollection);
    if (!bounds) return true; // пустая геометрия — проверять нечего
    const [ [ west, south ], [ east, north ] ] = bounds;
    return west >= -180 && east <= 180 && south >= -90 && north <= 90;
};
const readFileAsText = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(t`Не удалось прочитать файл`));
    reader.readAsText(file);
});

// Читает и валидирует файл. Возвращает { name, data } либо бросает Error
// с уже локализованным сообщением, готовым для нотификации.
export const parseGeoJsonFile = async file => {
    if (!hasAllowedExtension(file.name)) {
        throw new Error(t`Поддерживаются только файлы .geojson и .json`);
    }
    if (file.size > USER_GEOJSON_MAX_BYTES) {
        const limitMb = Math.round(USER_GEOJSON_MAX_BYTES / (1024 * 1024));
        throw new Error(t`Файл больше ${limitMb} МБ — слишком тяжёлый для отрисовки в браузере`);
    }

    const text = await readFileAsText(file);
    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch (e) {
        throw new Error(t`Файл не является корректным JSON`);
    }

    const featureCollection = toFeatureCollection(parsed);
    if (!featureCollection.features.length) {
        throw new Error(t`В файле нет объектов GeoJSON`);
    }
    if (!geometryKinds(featureCollection).size) {
        throw new Error(t`В файле нет поддерживаемых геометрий (точки, линии, полигоны)`);
    }
    if (!isWgs84(featureCollection)) {
        throw new Error(t`Координаты вне WGS84 — поддерживается только EPSG:4326`);
    }

    return { name: layerNameFromFile(file.name), data: featureCollection };
};

// Уникальный id слоя: если слой с таким именем уже загружен, добавляем счётчик.
export const buildUniqueLayerId = (name, existingIds) => {
    const base = `${USER_LAYER_PREFIX}${name}`;
    if (!existingIds.includes(base)) return base;
    let n = 2;
    while (existingIds.includes(`${base} (${n})`)) n++;
    return `${base} (${n})`;
};

// Стилевые описания mapbox-слоёв по типу геометрии. Один цвет на все пользовательские
// слои, заливка полупрозрачная — чтобы подложка и слои под ней читались.
const styleByKind = {
    polygon: id => ([
        {
            id,
            type: 'fill',
            paint: {
                'fill-color': USER_LAYER_COLOR,
                'fill-opacity': USER_LAYER_FILL_OPACITY,
            },
        },
        {
            id: `${id}_обводка`,
            type: 'line',
            paint: {
                'line-color': USER_LAYER_COLOR,
                'line-width': 1.5,
            },
        },
    ]),
    line: id => ([{
        id: `${id}_линии`,
        type: 'line',
        paint: {
            'line-color': USER_LAYER_COLOR,
            'line-width': 2,
        },
    }]),
    point: id => ([{
        id: `${id}_точки`,
        type: 'circle',
        paint: {
            'circle-color': USER_LAYER_COLOR,
            'circle-radius': 5,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
        },
    }]),
};

// Строит определение слоя в формате constants/layers.js: главный слой + additionalLayers,
// все на одном geojson-источнике. Порядок на карте расставит layerZOrder.
export const buildUserLayerDefinition = ({ id, name, data }) => {
    const kinds = geometryKinds(data);
    const sourceId = `${id}_source`;
    const specs = [ 'polygon', 'line', 'point' ]
        .filter(kind => kinds.has(kind))
        .flatMap(kind => styleByKind[kind](id));
    const withSource = (spec, index) => ({
        ...spec,
        // Источник добавляется один раз — на главном слое, остальные ссылаются по id
        source: index === 0 ? { id: sourceId, type: 'geojson', data } : sourceId,
        layout: { visibility: 'none' },
    });
    const [ main, ...rest ] = specs.map(withSource);
    return {
        ...main,
        // Переключатель в сайдбаре подписан именем файла; строка пользовательская,
        // в каталог переводов не попадает — i18n._ вернёт её как есть.
        label: name,
        isUserLayer: true,
        additionalLayers: rest,
    };
};
